/**
 * =========================================================================================
 * Organization Overview - Static Page
 * =========================================================================================
 */

import React, { useState, useMemo } from 'react';
import * as UTable from 'ubs-framework/v2/common/UTable';
import UDateRangePicker from 'ubs-framework/v2/UDateRangePicker';
import UToggleSwitch from 'ubs-framework/v2/UToggleSwitch';
import { notificationToast } from 'ubs-framework/v2/common/function';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';

// Realistic data — sub-company totals add up to parent totals
// Parent: totalEmployee = sum of sub totals, activeEmployee = sum of sub actives, etc.
// Active = Present + On Leave + Absent
const SUB_COMPANY_DATA = [
    // Sharma Industries Pvt. Ltd. (total: 520 = 185 + 210 + 125)
    { _id: '1', company: 'Sharma Industries Pvt. Ltd.', subCompany: '', totalEmployee: 520, activeEmployee: 376, inActiveEmployee: 144, present: 289, onLeave: 52, absent: 35, ownerName: 'Rajesh Sharma', isParent: true },
    { _id: '2', company: 'Sharma Industries Pvt. Ltd.', subCompany: 'Sharma Textiles', totalEmployee: 185, activeEmployee: 142, inActiveEmployee: 43, present: 112, onLeave: 18, absent: 12, ownerName: 'Priya Mehta' },
    { _id: '3', company: 'Sharma Industries Pvt. Ltd.', subCompany: 'Sharma Logistics', totalEmployee: 210, activeEmployee: 148, inActiveEmployee: 62, present: 109, onLeave: 24, absent: 15, ownerName: 'Amit Desai' },
    { _id: '4', company: 'Sharma Industries Pvt. Ltd.', subCompany: 'Sharma Infra', totalEmployee: 125, activeEmployee: 86, inActiveEmployee: 39, present: 68, onLeave: 10, absent: 8, ownerName: 'Sunita Joshi' },

    // Patel Solutions (total: 430 = 160 + 155 + 115)
    { _id: '5', company: 'Patel Solutions', subCompany: '', totalEmployee: 430, activeEmployee: 318, inActiveEmployee: 112, present: 241, onLeave: 45, absent: 32, ownerName: 'Vikram Patel', isParent: true },
    { _id: '6', company: 'Patel Solutions', subCompany: 'Patel IT Services', totalEmployee: 160, activeEmployee: 121, inActiveEmployee: 39, present: 95, onLeave: 16, absent: 10, ownerName: 'Neha Kulkarni' },
    { _id: '7', company: 'Patel Solutions', subCompany: 'Patel Analytics', totalEmployee: 155, activeEmployee: 110, inActiveEmployee: 45, present: 82, onLeave: 15, absent: 13, ownerName: 'Rohit Verma' },
    { _id: '8', company: 'Patel Solutions', subCompany: 'Patel Cloud', totalEmployee: 115, activeEmployee: 87, inActiveEmployee: 28, present: 64, onLeave: 14, absent: 9, ownerName: 'Kavita Nair' },

    // Gupta Enterprises (total: 390 = 145 + 130 + 115)
    { _id: '9', company: 'Gupta Enterprises', subCompany: '', totalEmployee: 390, activeEmployee: 278, inActiveEmployee: 112, present: 210, onLeave: 40, absent: 28, ownerName: 'Deepak Gupta', isParent: true },
    { _id: '10', company: 'Gupta Enterprises', subCompany: 'Gupta Finance', totalEmployee: 145, activeEmployee: 105, inActiveEmployee: 40, present: 80, onLeave: 15, absent: 10, ownerName: 'Ananya Iyer' },
    { _id: '11', company: 'Gupta Enterprises', subCompany: 'Gupta Pharma', totalEmployee: 130, activeEmployee: 94, inActiveEmployee: 36, present: 70, onLeave: 14, absent: 10, ownerName: 'Manish Tiwari' },
    { _id: '12', company: 'Gupta Enterprises', subCompany: 'Gupta Retail', totalEmployee: 115, activeEmployee: 79, inActiveEmployee: 36, present: 60, onLeave: 11, absent: 8, ownerName: 'Ritu Agarwal' },
];

// Build default status map — all enabled
const DEFAULT_STATUS = {};
SUB_COMPANY_DATA.forEach(row => { DEFAULT_STATUS[row._id] = true; });

const OrganizationOverview = () => {
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [statusMap, setStatusMap] = useState(DEFAULT_STATUS);

    const handleToggle = (data) => {
        const id = data?._id;
        const isCurrentlyEnabled = !!statusMap[id];
        const name = data?.subCompany || data?.company;

        setStatusMap(prev => ({ ...prev, [id]: !isCurrentlyEnabled }));

        if (isCurrentlyEnabled) {
            notificationToast("warning", "warning", `${name} has been disabled`);
        } else {
            notificationToast("success", "success", `${name} has been enabled`);
        }
    };

    const filteredData = useMemo(() => {
        let data = SUB_COMPANY_DATA;

        if (search?.trim()) {
            const q = search.toLowerCase();
            data = data.filter(row =>
                row.company?.toLowerCase().includes(q) ||
                row.subCompany?.toLowerCase().includes(q) ||
                row.ownerName?.toLowerCase().includes(q)
            );
        }

        return data;
    }, [search]);

    const handleDateChange = (e) => {
        setDateRange({
            startDate: e?.startDate || null,
            endDate: e?.endDate || null,
        });
    };

    const header = useMemo(() => [
        {
            title: 'Company',
            param: 'company',
            render: data => (
                <span className={data?.isParent ? 'u_fw-600 u_color-title' : 'u_color-font'}>
                    {data?.isParent ? data?.company : ''}
                </span>
            )
        },
        {
            title: 'Sub Company',
            param: 'subCompany',
            render: data => <span>{data?.subCompany || '-'}</span>
        },
        {
            title: 'Total Employee',
            param: 'totalEmployee',
            render: data => <span className="u_fw-500">{data?.totalEmployee}</span>
        },
        {
            title: 'Active Employee',
            param: 'activeEmployee',
            render: data => <span className="u_color-green u_fw-500">{data?.activeEmployee}</span>
        },
        {
            title: 'In Active Employees',
            param: 'inActiveEmployee',
            render: data => <span className="u_color-red u_fw-500">{data?.inActiveEmployee}</span>
        },
        {
            title: 'Present',
            param: 'present',
            render: data => <span>{data?.present}</span>
        },
        {
            title: 'On Leave',
            param: 'onLeave',
            render: data => <span>{data?.onLeave}</span>
        },
        {
            title: 'Absent',
            param: 'absent',
            render: data => <span>{data?.absent}</span>
        },
        {
            title: 'Owner Name',
            param: 'ownerName',
            render: data => <span className="u_fw-500">{data?.ownerName}</span>
        },
        {
            title: 'Status',
            param: 'status',
            render: data => (
                <UToggleSwitch
                    id={`status-toggle-${data?._id}`}
                    checked={!!statusMap[data?._id]}
                    onChange={() => handleToggle(data)}
                />
            )
        },
    ], [statusMap]);

    return (
        <div className="u_flex u_flex-col u_h-full u_p-20">
            <div className="u_bg-white u_radius-8 u_b-light u_flex u_flex-col u_flex-1-0 u_overflow-hidden">
                <div className="u_p-20 u_pb-0">
                    <div className="u_flex-align u_content-between u_mb-16">
                        <h4 className="u_font-18 u_fw-700 u_color-title">Organization Overview</h4>
                    </div>

                    <div className="u_flex-align u_mb-16" style={{ gap: '12px' }}>
                        <div className="u_flex-1-0" style={{ maxWidth: '300px' }}>
                            <div className="u_flex-align u_b-light u_radius-5 u_px-10 u_bg-thead" style={{ height: '36px' }}>
                                <Icon.Search size={16} fill={COLOR.u_icon_bg} className="u_mr-8" />
                                <input
                                    className="u_w-full u_font-14"
                                    placeholder="Search company, sub company or owner..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ border: 'none', outline: 'none', background: 'transparent' }}
                                />
                            </div>
                        </div>
                        <div className="u_filter u_filter-daterange">
                            <UDateRangePicker
                                filterName="Date"
                                maxDate={new Date()}
                                startDate={dateRange.startDate}
                                endDate={dateRange.endDate}
                                onClose={handleDateChange}
                                isClearable={true}
                                calendarPosition="bottom left"
                                extraRanges={true}
                            />
                        </div>
                    </div>
                </div>

                <div className="u_flex u_flex-col u_flex-1-0 u_overflow-auto">
                    <UTable.Table
                        header={header}
                        data={filteredData}
                        dataLoading={false}
                        skeletonColumn={10}
                        rowClassName={(data) => data?.isParent ? 'u_parent-row-highlight' : ''}
                        className="u_table-resource u_table-resource-nowrap u_table-resource-no-sticky u_table-resource-first-column-sticky u_alert-list org-overview-table"
                    />
                </div>
            </div>
        </div>
    );
};

export default OrganizationOverview;
